import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { T, ddmon, FOCUS } from "../clients/clientsTokens";
import { Card, CardHead, Secondary, Pill, Tag, DotsMenu } from "../clients/record/recordBits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.muted };
const INPUT = "w-full h-9 rounded-[7px] px-2.5 text-[13px] mt-1 bg-white";
const EDGE = { border: `1px solid ${T.control}`, color: T.ink };
const TYPES = [
  ["action", "Action"], ["task", "Task"], ["follow_up", "Follow-up"],
  ["check_in", "Check-in"], ["health_check", "Health check"],
];

export const myName = () => {
  try { return JSON.parse(localStorage.getItem("equipr_session") || "{}").name || ""; } catch { return ""; }
};

/* A to-do raised here is linked to the record and lands in the owner's My Work. */
const TodoDialog = ({ linkType, linkId, linkName, onClose, onSaved }) => {
  const [f, setF] = useState({ text: "", type: "action", owner: myName(), due_date: "" });
  const [reps, setReps] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const me = myName();
    axios.get(`${API}/agents/mapping`)
      .then((r) => {
        const names = (r.data.agents || []).filter((x) => x.id !== "emergency").map((x) => x.name).filter(Boolean);
        // Keep your own name in the list even if you aren't in the agent mapping,
        // otherwise the picker reads "Unassigned" while it is in fact assigned to you.
        setReps(me && !names.includes(me) ? [me, ...names] : names);
      })
      .catch(() => setReps(me ? [me] : []));
  }, []);

  const points = f.text.split("\n").map((s) => s.trim()).filter(Boolean);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/todos`, {
        points, title: points[0], type: f.type, status: "open",
        due_date: f.due_date || null,
        owner_name: f.owner || null, owner_names: f.owner ? [f.owner] : [],
        link_type: linkType, link_id: linkId, link_name: linkName || linkId,
      });
      toast.success(f.owner === myName() ? "Added to your to-dos" : `To-do added for ${f.owner || "nobody"}`);
      await onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add that to-do"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="entity-todo-dialog">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.28)" }} onClick={onClose} />
      <div className="relative h-full w-full max-w-[440px] flex flex-col"
        style={{ background: T.card, borderLeft: `1px solid ${T.border}` }}>
        <div className="px-5 py-4" style={{ borderBottom: `1px solid ${T.dividerInner}` }}>
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 17, fontWeight: 800, color: T.ink }}>Add a to-do</div>
          <div className="text-[12.5px]" style={{ color: T.ink2 }}>
            Linked to {linkName || linkId} and shown in the owner's My Work.
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3.5">
          <div>
            <div style={LABEL}>To-do</div>
            <textarea rows={3} value={f.text} autoFocus data-testid="entity-todo-text"
              onChange={(e) => setF({ ...f, text: e.target.value })}
              placeholder="What needs doing? Put each separate point on its own line."
              className={`w-full rounded-[7px] px-2.5 py-2 text-[13px] mt-1 ${FOCUS}`} style={EDGE} />
            {points.length > 1 && (
              <div className="text-[11.5px] mt-1" style={{ color: T.ink2 }}>
                Line 1 becomes the title; the other {points.length - 1} sit behind it as points.
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div style={LABEL}>Category</div>
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}
                data-testid="entity-todo-type" className={`${INPUT} ${FOCUS}`} style={EDGE}>
                {TYPES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div>
              <div style={LABEL}>Due date</div>
              <input type="date" value={f.due_date} data-testid="entity-todo-due"
                onChange={(e) => setF({ ...f, due_date: e.target.value })} className={`${INPUT} ${FOCUS}`} style={EDGE} />
            </div>
          </div>
          <div>
            <div style={LABEL}>Owner</div>
            <select value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })}
              data-testid="entity-todo-owner" className={`${INPUT} ${FOCUS}`} style={EDGE}>
              <option value="">Unassigned</option>
              {reps.map((r) => <option key={r} value={r}>{r === myName() ? `${r} (you)` : r}</option>)}
            </select>
          </div>
          {f.type === "health_check" && (
            <div className="text-[11.5px] rounded-[7px] px-2.5 py-2" data-testid="entity-todo-hc-note"
              style={{ background: T.inset, border: `1px solid ${T.dividerInner}`, color: T.ink2 }}>
              Health-check to-dos sit alongside the ones the cadence raises, and only close when a
              real health check is recorded against this record.
            </div>
          )}
        </div>
        <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: T.panel, borderTop: `1px solid ${T.dividerInner}` }}>
          <button onClick={onClose} className="h-9 px-3 text-[13px]" style={{ color: T.ink2 }}>Cancel</button>
          <button onClick={save} disabled={busy || !points.length} data-testid="entity-todo-save"
            className="ml-auto h-9 px-4 rounded-[7px] text-[12px] font-bold uppercase disabled:opacity-60"
            style={{ background: T.accent, border: `1px solid ${T.accentEdge}`, color: "#000", letterSpacing: "0.04em" }}>
            {busy ? "Adding…" : "Add to-do"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* Shared by the client and prospect records so both sides of the funnel match. */
export const EntityTodosCard = ({ linkType, linkId, linkName, refreshKey, onRecordCheck, testid = "entity-todos" }) => {
  const [todos, setTodos] = useState([]);
  const [dlg, setDlg] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/todos`, {
        params: { link_type: linkType, link_id: linkId, include_done: false },
      });
      setTodos(r.data.todos || []);
    } catch { setTodos([]); }
  }, [linkType, linkId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const complete = async (t) => {
    if (t.type === "health_check") {
      if (onRecordCheck) { onRecordCheck(); return; }
      toast.info("A health check closes when you record the check itself.");
      return;
    }
    try { await axios.patch(`${API}/todos/${t.id}`, { status: "done" }); toast.success("To-do done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not tick that off"); }
  };
  const remove = async (t) => {
    try { await axios.delete(`${API}/todos/${t.id}`); toast.success("To-do deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete that to-do"); }
  };

  return (
    <>
      <Card testid={testid}>
        <CardHead title="To Do" count={todos.length}
          action={<Secondary onClick={() => setDlg(true)} testid="todos-add">Add</Secondary>} />
        {todos.length === 0 ? (
          <div className="px-4 py-5 text-[13px] font-semibold" style={{ color: T.ink }} data-testid="todos-empty">
            No open to-dos for this {linkType}.
          </div>
        ) : todos.map((t) => {
          const overdue = t.due_date && String(t.due_date).slice(0, 10) < today;
          const hc = t.type === "health_check";
          return (
            <div key={t.id} className="px-4 py-2.5 flex items-start gap-2.5" data-testid={`entity-todo-row-${t.id}`}
              style={{ borderBottom: `1px solid ${T.dividerRow}` }}>
              <button onClick={() => complete(t)} data-testid={`entity-todo-done-${t.id}`}
                title={hc ? "Record the health check" : "Mark done"}
                className="mt-[3px] w-[15px] h-[15px] rounded-full shrink-0"
                style={{ border: `1.5px solid ${T.control}`, background: "#fff" }} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold" style={{ color: T.ink }}>{t.title}</div>
                <div className="text-[11.5px] flex items-center gap-1.5 flex-wrap" style={{ color: T.ink2 }}>
                  {t.type !== "action" && <Tag>{(t.type_label || t.type).replace(/_/g, " ")}</Tag>}
                  <span>{(t.assignee || {}).name || t.owner_name || "Unassigned"}</span>
                  <span style={{ color: T.disabled }}>·</span>
                  <span className="mono" style={{ color: overdue ? T.destructive : T.ink2 }}>
                    {t.due_date ? ddmon(t.due_date) : "No date"}
                  </span>
                  {overdue && <Pill text="Overdue" tone="overdue" />}
                </div>
              </div>
              <DotsMenu size={26} testid={`entity-todo-menu-${t.id}`} items={[
                { label: hc ? "Record the health check" : "Mark done", onClick: () => complete(t),
                  testid: `entity-todo-menu-done-${t.id}` },
                { label: "Open in To-dos", onClick: () => { window.location.href = "/company/to-dos"; } },
                { label: "Delete to-do", danger: true, onClick: () => remove(t),
                  hint: hc ? "The cadence will raise it again while a check is due."
                           : "Removes it for everyone. Cannot be undone." },
              ]} />
            </div>
          );
        })}
      </Card>
      {dlg && <TodoDialog linkType={linkType} linkId={linkId} linkName={linkName}
        onClose={() => setDlg(false)} onSaved={load} />}
    </>
  );
};
